import React, { useState } from "react";
import { createPortal } from "react-dom";
import { CalendarClock, MessageCircle, Send, X } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { lp } from "../lib/seo";
import { LegalConsent } from "./LegalConsent";
import { COUNTRY_CODES } from "../lib/countryCodes";
import { whatsappLink } from "../lib/contact";

const browserCountryCode = () => {
  if (typeof navigator === "undefined") {
    return "AE";
  }

  const locale = navigator.language || "";
  try {
    const region = new Intl.Locale(locale).region;
    return COUNTRY_CODES.some((country) => country.code === region) ? region : "AE";
  } catch {
    const region = locale.split("-")[1]?.toUpperCase();
    return COUNTRY_CODES.some((country) => country.code === region) ? region : "AE";
  }
};

export default function QuickReserve({ car, testIdSuffix = "" }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ start: "", end: "", name: "", phone: "" });
  const [consent, setConsent] = useState({ rental_terms_accepted: false, privacy_policy_accepted: false });
  const [phoneCountry, setPhoneCountry] = useState("AE");
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Dubai" }).format(new Date());
  const dialCode = COUNTRY_CODES.find((country) => country.code === phoneCountry)?.dial || "+971";
  const carLabel = [car.brand, car.model].filter(Boolean).join(" ") || car.name;
  const whatsappMessage = `Hi Silver Drive, I'd like to reserve the ${carLabel}. Please share availability.`;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  React.useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    return () => { document.body.style.overflow = previous; window.removeEventListener("keydown", closeOnEscape); };
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setPhoneCountry(browserCountryCode());
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start || !form.end || !form.name.trim() || !form.phone.trim()) {
      toast.error(t.detail.selectDates);
      return;
    }
    if (!consent.rental_terms_accepted || !consent.privacy_policy_accepted) { toast.error(t.legal.consentRequired); return; }
    const parts = form.name.trim().split(" ");
    setSaving(true);
    try {
      await api.post("/bookings", {
        car_id: car.id,
        start_date: form.start,
        end_date: form.end,
        first_name: parts[0],
        last_name: parts.slice(1).join(" ") || parts[0],
        phone: `${dialCode} ${form.phone.trim()}`.trim(),
        ...consent,
      });
      setOpen(false);
      navigate(lp("/thank-you"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        role="group"
        aria-label={`${t.quick.reserve} ${carLabel}`}
        data-testid={`quick-reserve-actions-${car.id}${testIdSuffix ? `-${testIdSuffix}` : ""}`}
        className="mt-3 flex min-h-12 w-full overflow-hidden border border-teal-800"
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`quick-reserve-btn-${car.id}${testIdSuffix ? `-${testIdSuffix}` : ""}`}
          className="flex min-w-0 flex-[6] items-center justify-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-teal-800 transition-colors duration-200 hover:bg-teal-800 hover:text-white"
        >
          <CalendarClock size={15} /> {t.quick.reserve}
        </button>
        <a
          href={whatsappLink(whatsappMessage)}
          target="_blank"
          rel="noopener noreferrer"
          title={`WhatsApp: ${carLabel}`}
          aria-label={`Open WhatsApp for ${carLabel}`}
          data-testid={`quick-reserve-whatsapp-${car.id}${testIdSuffix ? `-${testIdSuffix}` : ""}`}
          data-car-name={carLabel}
          data-link-placement={`quick-reserve-whatsapp-${car.id}`}
          className="flex min-w-12 flex-[1] items-center justify-center border-l border-teal-800 bg-[#25D366] text-white transition-[background-color,transform] duration-200 hover:bg-[#1ebe5d] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-950"
        >
          <MessageCircle size={19} aria-hidden="true" />
        </a>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-[300] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onMouseDown={() => setOpen(false)}>
          <div
            className="max-h-[88dvh] w-full max-w-md overflow-y-auto rounded-t-xl bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl sm:max-h-[90dvh] sm:rounded-none sm:p-6"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t.quick.title}
            data-testid={`quick-reserve-modal-${car.id}`}
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="font-serif-lux text-xl font-bold text-slate-900">{t.quick.title}</h3>
                <p className="mt-1 text-sm text-slate-500">{car.brand} {car.model}</p>
                <p
                  data-testid="quick-reserve-request-destination"
                  className="mt-2 text-xs leading-relaxed text-teal-700"
                >
                  {t.quick.requestDestination}
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} data-testid="quick-reserve-close" className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid={`quick-reserve-form-${car.id}`}>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-500">{t.quick.start}</span>
                  <input type="date" required min={today} value={form.start} onChange={set("start")} data-testid="quick-reserve-start" className="h-11 w-full border border-slate-300 px-3 text-sm outline-none focus:border-teal-600" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-500">{t.quick.end}</span>
                  <input type="date" required min={form.start || today} value={form.end} onChange={set("end")} data-testid="quick-reserve-end" className="h-11 w-full border border-slate-300 px-3 text-sm outline-none focus:border-teal-600" />
                </label>
              </div>
              <input required value={form.name} onChange={set("name")} placeholder={t.quick.name} data-testid="quick-reserve-name" className="h-11 w-full border border-slate-300 px-3 text-sm outline-none focus:border-teal-600" />
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-500" htmlFor="quick-reserve-phone">
                  {t.quick.phone}
                </label>
                <div className="grid grid-cols-[8.75rem_minmax(0,1fr)] gap-2">
                  <select
                    value={phoneCountry}
                    onChange={(event) => setPhoneCountry(event.target.value)}
                    aria-label={t.quick.phoneCountry}
                    data-testid="quick-reserve-phone-country"
                    className="h-11 min-w-0 border border-slate-300 bg-white px-2 text-sm outline-none focus:border-teal-600"
                  >
                    {COUNTRY_CODES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.dial}
                      </option>
                    ))}
                  </select>
                  <input
                    id="quick-reserve-phone"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    type="tel"
                    inputMode="tel"
                    placeholder={t.detail.phonePlaceholder}
                    data-testid="quick-reserve-phone"
                    className="h-11 min-w-0 border border-slate-300 px-3 text-sm outline-none focus:border-teal-600"
                  />
                </div>
              </div>
              <LegalConsent value={consent} onChange={setConsent} testId="quick-reserve" />
              <button type="submit" disabled={saving} data-testid={`quick-reserve-submit-${car.id}`} className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-700 px-4 py-3 text-sm font-bold text-white transition-[filter] duration-300 hover:brightness-110 disabled:opacity-60">
                <Send size={15} /> {saving ? "..." : t.quick.submit}
              </button>
            </form>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
